import { useEffect, useRef, useState } from 'react';
import { apiGet } from '@/services/api';
import { PUBLIC_RECORD, publicRecordState } from '@/features/publicsite/publicRecord';
import type { PublicRecord } from '@/features/publicsite/types/PublicRecord';
import { useLanguage } from '@/i18n/LanguageContext';

/**
 * Loads a public read once, and again whenever the language changes.
 *
 * Lifted out of CatalogRoutes, where it was module-private and answered five catalogue pages,
 * while HomePage wrote the same three rules out by hand four times over. That duplication was not
 * inert: the hook catches a failed fetch and the hand-written copies did not, so a home-page strip
 * whose endpoint answered anything but 200 kept its "still loading" state for good — heading, then
 * nothing, with no end to it. One implementation cannot drift from itself.
 *
 * The language dependency is not cosmetic: services/api.ts appends `lang` to public reads and the
 * server resolves administered content against it, so a list fetched in Romanian stays Romanian
 * until it is asked for again.
 *
 * `null` means "not loaded yet" and is distinct from the empty value, which means "loaded, and
 * empty" — callers print an empty state only for the second, because saying "no records yet"
 * while the request is still in flight tells the visitor something untrue.
 */
export function usePublicData<T>(url: string, whenUnavailable: T): T | null {
  const { language } = useLanguage();
  const [data, setData] = useState<T | null>(null);
  // Kept in a ref rather than named among the dependencies. It is a value callers naturally write
  // inline, and an object literal is a new object on every render — listed as a dependency it
  // would restart the request on every render. The dependencies stay the two things that actually
  // change what is being asked for.
  const fallback = useRef(whenUnavailable);
  fallback.current = whenUnavailable;

  useEffect(() => {
    let live = true;
    setData(null);
    apiGet<T>(url)
      .then((r) => {
        if (live) setData(r);
      })
      .catch(() => {
        // A failed fetch (rate limit, a transient network error, an endpoint the deployed backend
        // does not serve yet) must not leave the caller spinning forever with `data` stuck at
        // null — fall back to the empty value rather than an indefinite loading placeholder.
        if (live) setData(fallback.current);
      });
    return () => {
      live = false;
    };
  }, [url, language]);

  return data;
}

/**
 * Shared across every caller because none of them writes to it, and because a fresh `[]` per
 * render would make the fallback a new value each time for no reason.
 */
const NO_ITEMS: never[] = [];

/** {@link usePublicData} for the public reads that answer with a bare array. */
export function usePublicList<T>(url: string): T[] | null {
  return usePublicData<T[]>(url, NO_ITEMS);
}

/**
 * One public record, with the reason a read failed kept rather than flattened.
 *
 * {@link usePublicData} answers every failure with the same fallback, which is right for a list —
 * it shows nothing either way — and wrong for a page about a single thing: a fault would tell the
 * visitor that a live, published record does not exist. Here 404 and everything else stay apart,
 * so the page can say "there is no such institution" only when the server said so.
 *
 * Reloads on a language change for the same reason its sibling does: services/api.ts appends
 * `lang`, and the portfolio comes back in the reader's language.
 */
export function usePublicRecord<T>(url: string): PublicRecord<T> {
  const { language } = useLanguage();
  const [record, setRecord] = useState<PublicRecord<T>>({
    state: PUBLIC_RECORD.loading,
    item: null,
  });

  useEffect(() => {
    let live = true;
    setRecord({ state: PUBLIC_RECORD.loading, item: null });
    apiGet<T>(url)
      .then((r) => {
        if (live) setRecord({ state: PUBLIC_RECORD.found, item: r });
      })
      .catch((e: unknown) => {
        if (live) setRecord({ state: publicRecordState(e), item: null });
      });
    return () => {
      live = false;
    };
  }, [url, language]);

  return record;
}
